import { QuestionGroupStatus } from '@exam-management/graphql';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import { useNavigate } from '@exam-management/router';
import {
  DeleteOutlineIcon,
  EditOutlinedIcon,
  MoreMenuIconButton,
  type MoreMenuIconButtonOption,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useItemSetQuestionDetails } from '../../providers';
import { ItemSetSubQuestionDeletionModalLogic } from '../item-set-sub-question-deletion-modal-logic';

interface BaseMoreMenuOptions extends MoreMenuIconButtonOption {
  status: QuestionGroupStatus[];
  requirePermissions: string[];
}

interface ItemSetSubQuestionMoreMenuProps {
  subQuestionId: string;
  taskId: string;
}

export default function ItemSetSubQuestionMoreMenu(
  props: Readonly<ItemSetSubQuestionMoreMenuProps>,
) {
  const { subQuestionId, taskId } = props;
  const location = useLocation();
  const matchedTaskManagement = matchPath('/task-management/*', location.pathname);

  const { roles } = useQuestionCreationTaskDetails();
  const { itemSet } = useItemSetQuestionDetails();

  const navigate = useNavigate();

  const [isDeleteModalOpen, deleteModalHandlers] = useDisclosure(false);
  const { open: openDeleteModal, close: closeDeleteModal } = deleteModalHandlers;

  const baseMoreMenuOptions = useMemo<BaseMoreMenuOptions[]>(
    () => [
      {
        icon: <EditOutlinedIcon />,
        label: 'Edit',
        sx: (theme) => ({ color: theme.palette.text.secondary }),
        status: [QuestionGroupStatus.InDevelopment],
        requirePermissions: [],
        onClick: () => {
          navigate(
            matchedTaskManagement
              ? '/task-management/question-creation/:taskId/item-set/:questionId/:subQuestionId'
              : '/my-task/question-creation/:taskId/item-set/:questionId/:subQuestionId',
            {
              params: {
                taskId: taskId,
                questionId: itemSet.id,
                subQuestionId,
              },
            },
          );
        },
      },
      // TODO: Uncomment when the duplicate functionality is ready
      // {
      //   icon: <ContentCopyRoundedIcon />,
      //   label: 'Duplicate',
      //   sx: (theme) => ({ color: theme.palette.text.secondary }),
      //   status: [QuestionGroupStatus.Draft],
      //   requirePermissions: [APP_PERMISSIONS.QBANK.QUESTION.CREATE],
      //   onClick: () => {
      //     // TODO: Handle duplicate action
      //     logger.info('Duplicate action clicked with id:' + subQuestionId);
      //   },
      // },
      {
        icon: <DeleteOutlineIcon />,
        label: 'Delete',
        sx: (theme) => ({ color: theme.palette.error.main }),
        status: [QuestionGroupStatus.InDevelopment],
        requirePermissions: [],
        onClick: openDeleteModal,
      },
    ],
    [navigate, openDeleteModal, taskId, itemSet.id, subQuestionId, matchedTaskManagement],
  );

  const moreMenuOptions = useMemo(
    () =>
      baseMoreMenuOptions
        .filter((option) => option.status.includes(itemSet.status))
        .map(({ icon, label, sx, onClick }) => ({
          icon,
          label,
          sx,
          onClick,
        })),
    [baseMoreMenuOptions, itemSet.status],
  );

  if (moreMenuOptions.length === 0 || !roles.isAssignee) {
    return null;
  }

  return (
    <>
      <MoreMenuIconButton
        slotProps={{
          iconButton: { sx: (theme) => ({ color: theme.palette.text.secondary }) },
        }}
        options={moreMenuOptions}
      />
      <ItemSetSubQuestionDeletionModalLogic
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        subQuestionId={subQuestionId}
      />
    </>
  );
}
